import axios from 'axios';
import { encodeObject } from './utils';

export default {
  // Article Management
  findArticles: (payload) => {
    return axios.get('/api/admin/sales/articles', { params: payload })
      .then(response => ({
        articles: Array.isArray(response.data) ? response.data : [],
      }));
  },

  getArticle: (articleId) => {
    return axios.get(`/api/admin/sales/articles/${articleId}`)
      .then(response => ({
        article: response.data || {},
      }));
  },

  createArticle: (article) => {
    return axios.post('/api/admin/sales/articles', article)
      .then(response => ({
        article: response.data || {},
      }));
  },

  updateArticle: (articleId, article) => {
    return axios.put(`/api/admin/sales/articles/${articleId}`, article)
      .then(response => ({
        article: response.data || {},
      }));
  },

  deleteArticle: (articleId) => {
    return axios.delete(`/api/admin/sales/articles/${articleId}`)
      .then(response => ({
        article: response.data || {},
      }));
  },

  // Package Management
  findPackages: (payload) => {
    return axios.get('/api/admin/sales/packages', { params: payload })
      .then(response => ({
        packages: Array.isArray(response.data) ? response.data : [],
      }));
  },

  getPackage: (packageId) => {
    return axios.get(`/api/admin/sales/packages/${packageId}`)
      .then(response => ({
        package: response.data || {},
      }));
  },

  createPackage: (packageData) => {
    return axios.post('/api/admin/sales/packages', packageData)
      .then(response => ({
        package: response.data || {},
      }));
  },

  updatePackage: (packageId, packageData) => {
    return axios.put(`/api/admin/sales/packages/${packageId}`, packageData)
      .then(response => ({
        package: response.data || {},
      }));
  },

  deletePackage: (packageId) => {
    return axios.delete(`/api/admin/sales/packages/${packageId}`)
      .then(response => ({
        package: response.data || {},
      }));
  },

  // Quote Management
  findQuotes: (payload) => {
    return axios.get('/api/admin/sales/quotes', { params: payload })
      .then(response => ({
        quotes: Array.isArray(response.data) ? response.data : [],
      }));
  },

  getQuote: (quoteId) => {
    return axios.get(`/api/admin/sales/quotes/${quoteId}`)
      .then(response => ({
        quote: response.data.quote || {},
      }));
  },

  createQuote: (quoteData) => {
    return axios.post('/api/admin/sales/quotes', quoteData)
      .then(response => ({
        quote: response.data.quote || {},
      }));
  },

  updateQuote: (quoteId, quoteData) => {
    return axios.put(`/api/admin/sales/quotes/${quoteId}`, quoteData)
      .then(response => ({
        quote: response.data.quote || {},
      }));
  },

  // options: { recalculatePricing, resetDates, shiftDurations } - all off copies the quote as is
  copyQuote: (quoteId, options = {}) => {
    return axios.post(`/api/admin/sales/quotes/${quoteId}/copy`, options)
      .then(response => ({
        quote: response.data.quote || {},
      }));
  },

  deleteQuote: (quoteId) => {
    return axios.delete(`/api/admin/sales/quotes/${quoteId}`)
      .then(response => ({
        success: true,
      }));
  },

  calculateQuote: (quoteData) => {
    return axios.post('/api/admin/sales/quotes/calculate', quoteData)
      .then(response => ({
        quote: response.data.quote || {},
      }));
  },



  requestApproval: (quoteId, data) => {
    return axios.post(`/api/admin/sales/quotes/${quoteId}/request-approval`, data)
      .then(response => ({
        quote: response.data.quote || {},
      }));
  },

  // Statistics & Budget
  getForecast: (year) => {
    return axios.get('/api/admin/sales/statistics/forecast', { params: { year } })
      .then(response => ({
        year: response.data.year,
        currency: response.data.currency,
        months: response.data.months || [],
      }));
  },

  getProjectTimeline: (year) => {
    return axios.get('/api/admin/sales/statistics/projects', { params: { year } })
      .then(response => ({
        year: response.data.year,
        projects: response.data.projects || [],
      }));
  },

  getWonRejected: (year) => {
    return axios.get('/api/admin/sales/statistics/won-rejected', { params: { year } })
      .then(response => ({
        year: response.data.year,
        months: response.data.months || [],
        medianDaysToWin: response.data.medianDaysToWin ?? null,
        medianDaysToReject: response.data.medianDaysToReject ?? null,
      }));
  },

  getBudget: (year) => {
    return axios.get('/api/admin/sales/budget', { params: { year } })
      .then(response => ({
        year: response.data.year,
        months: response.data.months || [],
      }));
  },

  updateBudget: (year, months) => {
    return axios.put('/api/admin/sales/budget', { year, months })
      .then(response => ({
        year: response.data.year,
        months: response.data.months || [],
      }));
  },

  // Customer Management (Parent Companies)
  findCustomers: (payload) => {
    return axios.put('/api/admin/parent-companies', payload)
      .then(response => ({
        customers: response.data.parentCompanies || [],
      }));
  },

  createCustomer: (customerData) => {
    return axios.post('/api/admin/parent-companies', customerData)
      .then(response => ({
        customer: response.data.parentCompany || {},
      }));
  },

  updateCustomer: (customerId, customerData) => {
    return axios.put(`/api/admin/parent-companies/${customerId}`, customerData)
      .then(response => ({
        customer: response.data.parentCompany || {},
      }));
  },

  // Customer Pricing Rules
  findCustomerPricingRules: (customerId) => {
    return axios.get(`/api/admin/sales/customer-pricing/${customerId}`)
      .then(response => {
        // Handle both response formats:
        // 1. Direct array: [rules...]
        // 2. Object with pricingRules property: {pricingRules: [rules...]}
        let pricingRules;
        if (Array.isArray(response.data)) {
          pricingRules = response.data;
        } else if (response.data && Array.isArray(response.data.pricingRules)) {
          pricingRules = response.data.pricingRules;
        } else {
          pricingRules = [];
        }
        
        return {
          pricingRules: pricingRules
        };
      });
  },

  setCustomerPricingRules: (customerId, rules) => {
    return axios.post(`/api/admin/sales/customer-pricing/${customerId}`, { pricingRules: rules })
      .then(response => ({
        pricingRules: Array.isArray(response.data) ? response.data : [],
      }));
  },

  // System Configuration
  // { <currency>: rate from `from` } for every currency the exchange rate cronjob stores
  getExchangeRates: (from = 'SEK') => {
    return axios.get('/api/admin/sales/system-config/exchange-rates', { params: { from } })
      .then(response => ({
        rates: response.data.rates || {},
        lastUpdated: response.data.lastUpdated,
      }));
  },

  updateExchangeRates: (rates) => {
    return axios.put('/api/admin/sales/system-config/exchange-rates', rates)
      .then(response => ({
        rates: response.data || {},
      }));
  },

  // PDF Generation
  downloadQuotePDF: (quoteId) => {
    return axios.get(`/api/admin/sales/quotes/${quoteId}/pdf`, {
      responseType: 'blob'
    }).then(response => {
      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = `quote-${quoteId}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      return {
        success: true,
        blob: response.data,
        filename: filename
      };
    });
  },

  getQuotePDFPreview: (quoteId) => {
    return axios.get(`/api/admin/sales/quotes/${quoteId}/pdf/preview`)
      .then(response => ({
        success: true,
        pdf: response.data.pdf,
        filename: response.data.filename
      }));
  },
}; 